// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {ActionList, ActionMenu, Button, FormControl, Link, Portal, Radio, RadioGroup} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {useCallback, useState} from 'react'
import {useRelayEnvironment} from 'react-relay'

import type {AbuseReportReason} from '../../mutations/__generated__/submitAbuseReportMutation.graphql'
import {submitAbuseReportMutation} from '../../mutations/submit-abuse-report-mutation'
import styles from './ReportContentDialog.module.css'

export type ReportContentDialogProps = {
  owner: string
  ownerUrl: string
  reportUrl?: string
  contentId: string
  onClose: () => void
  contentType?: ContentType
}

type ReportReason = AbuseReportReason | 'Choose a reason'
type ReportType = 'admins' | 'support'
type ContentType = 'issue' | 'comment' | 'pull request' | 'content'

export const ReportContentDialog = ({
  owner,
  ownerUrl,
  reportUrl,
  contentId,
  onClose,
  contentType = 'content',
}: ReportContentDialogProps) => {
  const environment = useRelayEnvironment()
  const {addToast} = useToastContext()
  const [reportReason, setReportReason] = useState<ReportReason>('Choose a reason')
  const [isLoading, setIsLoading] = useState(false)
  const [reportType, setReportType] = useState<ReportType>('admins')

  const onSubmitReportToRepositoryAdmins = useCallback(() => {
    setIsLoading(true)
    const reason = getReason(reportReason)

    submitAbuseReportMutation({
      environment,
      input: {
        reportedContentId: contentId,
        reason,
      },
      onError(error) {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          type: 'error',
          message: `Unable to submit report: ${error}`,
        })
        setIsLoading(false)
      },
      onCompleted() {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          type: 'success',
          message: 'Report submitted',
        })
        setIsLoading(false)
        onClose()
      },
    })
  }, [addToast, contentId, environment, onClose, reportReason])

  const handleReportTypeChange = (selectedValue: string | null) => {
    setReportType(selectedValue as ReportType)
  }

  const reportToMaintainers = (
    <div className={styles.ReportContentContainer}>
      <div>
        <p>
          <span>This {contentType} violates </span>
          <Link href={ownerUrl}>{`@${owner}'s `}</Link>
          <span>Code of Conduct and should be submitted to the maintainers of this repository for review.</span>
        </p>
        <p>This report, as well as your username as the reporter, will be viewable by all admins of this repository.</p>
        <p>Choose a reason for reporting this {contentType}</p>
      </div>
      <div className={styles.ReportContentSpacing}>
        <ActionMenu>
          <ActionMenu.Button>{reportReason}</ActionMenu.Button>

          <ActionMenu.Overlay>
            <ActionList>
              <ActionList.Item onSelect={() => setReportReason('ABUSE')}>Abuse</ActionList.Item>
              <ActionList.Item onSelect={() => setReportReason('SPAM')}>Spam</ActionList.Item>
              <ActionList.Item onSelect={() => setReportReason('OFF_TOPIC')}>Off Topic</ActionList.Item>
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      </div>
      <div className={styles.ReportContentTopMargin}>
        <div className={styles.ReportContentHelpText}>
          <span>Learn more about </span>
          <Link href="https://docs.github.com/articles/reporting-abuse-or-spam">
            requesting that maintainers moderate content.
          </Link>
        </div>
        <Button
          variant={'danger'}
          onClick={() => onSubmitReportToRepositoryAdmins()}
          disabled={isLoading}
          className={styles.ReportContentSubmitButton}
        >
          Report to repository admins
        </Button>
      </div>
    </div>
  )

  const reportToSupport = (
    <div className={styles.ReportContentContainer}>
      <span>Contact GitHub Support about this user&apos;s spammy or abusive behavior.</span>
      <div className={styles.ReportContentFooterSpacing}>
        <div className={styles.ReportContentHelpText}>
          <span>Learn more about </span>
          <Link href="https://docs.github.com/articles/reporting-abuse-or-spam">reporting abuse to GitHub support</Link>
        </div>
        <Button
          variant={'danger'}
          as={'a'}
          href={reportUrl}
          disabled={isLoading}
          className={styles.ReportContentSubmitButton}
        >
          Report abuse to GitHub support
        </Button>
      </div>
    </div>
  )

  return (
    <Portal>
      <Dialog
        title={`Report ${contentType}`}
        width="xlarge"
        height="auto"
        onClose={onClose}
        className={styles.ReportContentDialog}
      >
        <div className={styles.ReportContentRadioContainer}>
          <RadioGroup name="choiceGroup" onChange={handleReportTypeChange}>
            <RadioGroup.Label className={styles.ReportContentRadioLabel}>
              Where would you like to report this {contentType} to?
            </RadioGroup.Label>
            <FormControl>
              <Radio value="admins" defaultChecked />
              <FormControl.Label>To repository admins</FormControl.Label>
            </FormControl>
            <FormControl>
              <Radio value="support" />
              <FormControl.Label>To GitHub support</FormControl.Label>
            </FormControl>
          </RadioGroup>
          {reportType === 'admins' && reportToMaintainers}
          {reportType === 'support' && reportToSupport}
        </div>
      </Dialog>
    </Portal>
  )
}

function getReason(reportReason: ReportReason): AbuseReportReason {
  if (reportReason === 'Choose a reason') {
    return 'UNSPECIFIED'
  }
  return reportReason
}

try{ ReportContentDialog.displayName ||= 'ReportContentDialog' } catch {}